import React, { type RefObject, useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { Portal } from '@pkg/components/Portal';
import { PortalArea } from '@pkg/components/PortalProvider';

import { Content } from './Content';
import { calculateLeft, calculateTop } from './utils';

import { DesktopWrapper } from './styles';

interface CoordsProps {
    left: number;
    top: number;
}

const DELAYED_SHOW_TIME = 100; //ms
const DELAYED_HIDE_TIME = 300; //ms

interface CollectionPeekProps {
    slug?: string;
    hovered?: boolean;
    linkElement?: RefObject<HTMLAnchorElement>;
    collection_name?: string;
    collection_image?: string;
    collection_count?: string;
}

export const CollectionPeek = ({
    slug,
    hovered,
    linkElement,
    collection_name,
    collection_image,
    collection_count,
}: CollectionPeekProps) => {
    const [show, setShow] = useState(false);
    const [isOver, setIsOver] = useState(false);
    const [coords, setCoords] = useState<CoordsProps | undefined>(undefined);

    const handleMouseOver = () => {
        setIsOver(true);
    };

    const handleMouseOut = () => {
        const timeoutId = setTimeout(() => {
            setIsOver(false);
        }, DELAYED_HIDE_TIME);
        return () => clearTimeout(timeoutId);
    };

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (hovered) {
            timeoutId = setTimeout(() => {
                setShow(true);
            }, DELAYED_SHOW_TIME);
        } else {
            timeoutId = setTimeout(() => {
                setShow(false);
            }, DELAYED_HIDE_TIME);
        }
        return () => clearTimeout(timeoutId);
    }, [hovered]);

    useEffect(() => {
        if (show && linkElement && linkElement.current) {
            const rect = linkElement.current.getBoundingClientRect();
            const left = calculateLeft(rect.left);
            const top = calculateTop(rect.top, rect.height);
            setCoords({ left, top });
        }
    }, [linkElement, show]);

    return (
        <AnimatePresence>
            {(show || isOver) && slug && (
                <Portal destination={PortalArea.Transition}>
                    <DesktopWrapper
                        onMouseEnter={handleMouseOver}
                        onMouseLeave={handleMouseOut}
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        $left={coords?.left}
                        $top={coords?.top}
                    >
                        <Content
                            slug={slug}
                            collection_name={collection_name}
                            collection_image={collection_image}
                            collection_count={collection_count}
                        />
                    </DesktopWrapper>
                </Portal>
            )}
        </AnimatePresence>
    );
};
